import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  fetchSubscription,
  fetchServiceAreaPostcodes,
  fetchServiceSettings,
  fetchPendingReturns
} from 'lib/requests';
import getSubscriptionId from 'lib/getSubscriptionId';

const ApiContext = createContext();

export function ApiProvider({ children }) {
  const [subscription, setSubscription] = useState();
  const [settings, setServiceSettings] = useState();
  const [postcodes, setPostcodes] = useState();
  const [pendingReturns, setPendingReturns] = useState();

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      const subscriptionId = getSubscriptionId();

      try {
        const promises = [fetchServiceAreaPostcodes(), fetchServiceSettings()];

        // only push subscription-related requests if id exists
        if (subscriptionId) {
          promises.unshift(fetchPendingReturns(subscriptionId));
          promises.unshift(fetchSubscription(subscriptionId));
        }

        const results = await Promise.all(promises);

        if (!cancelled) {
          if (subscriptionId) {
            const [
              subscriptionData,
              pendingReturns,
              postcodesData,
              settingsData
            ] = results;

            setSubscription(subscriptionData[0]);
            setPendingReturns(pendingReturns.map((x) => x.product.id));
            setPostcodes(postcodesData);
            setServiceSettings(settingsData);
          } else {
            const [postcodesData, settingsData] = results;
            setPostcodes(postcodesData);
            setServiceSettings(settingsData);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Check whether the given postcode or that of the subscription is
  // in the service area
  //
  function isWithinServiceArea(postcode) {
    if (!postcodes) return false;
    if (!postcode && !subscription?.zipcode) return false;

    let isWithin = false;
    const res = (postcode || subscription?.zipcode || '')
      .trim()
      .match(/(\d{4}).*/i);

    if (!res) return false;
    const [_, zipcode] = res;

    for (const region of Object.keys(postcodes)) {
      if (
        zipcode &&
        postcodes[region].map((x) => x.toString()).includes(zipcode.toString())
      ) {
        isWithin = true;

        break;
      }
    }

    return isWithin;
  }

  return (
    <ApiContext.Provider
      value={{
        subscription,
        settings,
        postcodes,
        pendingReturns,
        isWithinServiceArea
      }}>
      {children}
    </ApiContext.Provider>
  );
}

export function useApi() {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
}
